import { ChangeEventHandler, FC } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Input, Svg, Typography } from '@altermaind/web/ui';
import { GenericApiError } from '@/_shared/types/apiError';
import { Claims } from '../../constants';
import { ClaimsComponent } from '../Claims';
import { CtaContainer } from '../CtaContainer';
import { HeaderTableSkeleton } from './HeaderTableSkeleton';

type Props = {
  title: string;
  errorApi?: GenericApiError | undefined;
  loadingApi?: boolean | undefined;
  onChangeSearch?: ChangeEventHandler<HTMLInputElement> | undefined;
  addLabel?: string;
  onFilter?: (() => void) | undefined;
  onAdd?: (() => void) | undefined;
  onAddClaimKey?: Claims | undefined;
  onRefreshData?: (() => void) | undefined;
  'prefix-testid'?: string;
};

/**
 * @deprecated Use TableHeader instead.
 */
const HeaderTable: FC<Props> & {
  Skeleton: typeof HeaderTableSkeleton;
} = ({
  title,
  errorApi,
  loadingApi,
  onChangeSearch,
  addLabel = 'headerTable.new',
  onFilter,
  onAdd,
  onAddClaimKey,
  onRefreshData,
  'prefix-testid': prefixTestId,
}) => {
  const t = useTranslations();

  return (
    <Box display='flex' flexDirection='column'>
      <Box display='flex' justifyContent='space-between' width='100%' flexDirection='column'>
        <Box alignItems='center' justifyContent='space-between' mb='l' display='flex'>
          <Box alignItems='flex-start'>
            <Typography variant='h4'>{title}</Typography>
          </Box>
          {!errorApi && !loadingApi && (
            <Box display='flex' flexDirection='row' alignItems='flex-end'>
              {onRefreshData && (
                <Box ml='xs'>
                  <CtaContainer onRefreshData={onRefreshData} prefix-testid={prefixTestId} />
                </Box>
              )}
              {onChangeSearch && (
                <Box ml='xs'>
                  <Input
                    suffixSvg='search'
                    placeholder={t('headerTable.search')}
                    name='search'
                    onChange={onChangeSearch}
                    inputTextStyle={{ pr: 'l' }}
                    data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}input-search`}
                  />
                </Box>
              )}
              {onFilter && (
                <Button
                  data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-filter`}
                  onClick={onFilter}
                  type='button'
                  px='xs'
                  variant='primary'
                  backgroundColor='grey/dark/background'
                  ml='xs'
                >
                  <Svg name='filter' fill='primary/text' size='xs' />
                  <Typography
                    data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}header-table-title`}
                    as='span'
                    variant='p2/light'
                    ml='xxxs'
                  >
                    {t('headerTable.filter')}
                  </Typography>
                </Button>
              )}
              {onAdd && (
                <ClaimsComponent claimKey={onAddClaimKey}>
                  <Button
                    data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-add`}
                    ml='xs'
                    variant='positive'
                    px='xs'
                    onClick={onAdd}
                    type='button'
                  >
                    <Typography variant='p2' fontSize='xxl' color='primary'>
                      +
                    </Typography>
                    <Typography variant='p2' ml='xxxs' color='primary'>
                      {t(addLabel)}
                    </Typography>
                  </Button>
                </ClaimsComponent>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

HeaderTable.Skeleton = HeaderTableSkeleton;

export { HeaderTable };
